import { action, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { next } from '@ember/runloop';
import { service } from '@ember/service';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { PopupEvent, Theme } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { redirect } from 'webapp/utils/common';

import BillingPopupExpiredTrialComponent from 'webapp/components/billing/popup-expired-trial';
import type DesignerController from 'webapp/controllers/designer';

import type { ConfirmPopupDontShowAgainValueKey } from 'webapp/services/user-settings';

export default class DesignerRoute extends Route {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('flow/invite-users-promo')
	declare flowInviteUsersPromoService: Service.Flow.InviteUsersPromo;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	enteringTransition: PublicTransition | null = null;
	dontShowAgainKey: ConfirmPopupDontShowAgainValueKey = UserSettingsKey.EXIT_DESIGNER;
	hasConfirmedExit = false;

	override beforeModel(transition: PublicTransition) {
		// force dark theme in designer
		this.navigationService.createTheme(Theme.DARK);

		let skipDesignerMobileAppSuggestion,
			preventInitialRequests = false;

		try {
			const value = localStorage.getItem('skipDesignerMobileAppSuggestion');
			if (value) {
				skipDesignerMobileAppSuggestion = !!+value;
			}
		} catch (_e) {
			// just ignore, not important enough to warn user
		}

		if (
			skipDesignerMobileAppSuggestion !== true &&
			this.currentUserService.deviceString !== 'desktop' &&
			!this.accountService.whiteLabelingEnabled
		) {
			preventInitialRequests = true;

			// Aborting transition so we can close popup
			// also to open popup from url /layout-designer/:board_id without errors ...
			transition.abort();

			this.navigationService.createMobileAppSuggestionPopup().open({
				onMobileAppContinue: () => {
					const redirectUrl = `https://databox.com/${this.currentUserService.model?.lastLoginMobile ? 'open' : 'download'}`;

					redirect(redirectUrl);
				},
				onWebAppContinue: () => {
					transition.retry();
				},
			});
		}

		document.body.id = 'page-layoutdesigner';

		if (preventInitialRequests) {
			// Check because of runloop transition abort.
			return;
		}

		void this.mmMetricsService.requestMetrics();

		void this.mmScriptsService.requestScripts();

		void this.mmCustomQueriesService.requestCustomQueries();

		void this.mmGoalsService.requestSpaceGoals();

		void this.mmTemplatesService.requestBoardAccountTemplates();

		void this.mmBoardSharesService.requestBoardShares(); // for designer exit popup

		this.enteringTransition = transition;

		return this.navigationService.initialRequestsPromise;
	}

	override setupController(
		controller: DesignerController,
		model: unknown,
		transition: PublicTransition,
	): void {
		super.setupController(controller, model, transition);

		controller.enteringTransition = this.enteringTransition;

		if (controller.isBoardIndex || controller.allowBothSidebarsOpen) {
			controller.openLeftSidebar();
		}

		// Adding manualy, because 'designer' route gets redirected before didTransition kicks in
		this.analyticsService.pageFromPath('designer');

		this.eventManagerService.on(
			AppEvent.DESIGNER_BOARD_LOADED,
			controller,
			controller.boardLoaded,
		);
	}

	override resetController(controller: DesignerController, isExiting: boolean): void {
		$(window).off('resize.sidebars');

		if (isExiting) {
			next(() => {
				// keep boardState so we can finish operations like timyce onChange (data-story visualization)
				setProperties(controller, {
					board: null,
					loop: null,
					boardState: null,
					isLeftSidebarOpen: false,
					activeDatawallGrid: null,
				});
			});

			this.eventManagerService.off(AppEvent.DESIGNER_BOARD_LOADED, controller);
		}
	}

	@action
	override willTransition(transition: PublicTransition) {
		if (!transition.targetName?.includes('designer')) {
			const controller = this.controllerFor('designer') as DesignerController;
			const isBillingExpiredPopupOpen = this.popupService.isOpen(
				BillingPopupExpiredTrialComponent,
			);
			const openConfirmPopup =
				!this.hasConfirmedExit && !controller.board?.isNew && !isBillingExpiredPopupOpen;

			if (openConfirmPopup) {
				transition.abort();

				const confirmExitAndRetry = () => {
					this.hasConfirmedExit = true;
					transition.retry();
				};

				const checkToOpenConfirmPopup = () => {
					void this.popupService
						.checkToOpenConfirmPopup({
							onConfirm: () => {
								confirmExitAndRetry();
							},
							title: 'Exit the Dashboard designer?',
							text: 'All changes are successfully saved. Return at any time to keep editing. ',
							confirmAction: 'Yes, exit',
							cancelAction: 'No, stay here',
							isWarning: true,
							dontShowAgainKey: this.dontShowAgainKey,
						})
						.then((isConfirmed) => {
							if (isConfirmed) {
								confirmExitAndRetry();
							}
						});
				};

				next(this, checkToOpenConfirmPopup);
			} else {
				this.navigationService.createTheme(this.userSettingsService.colorMode);
				const activePopup = this.popupService.activePopup,
					openPopup = () => {
						next(this, () => {
							this.flowInviteUsersPromoService.checkToOpenBoardPopup();
						});
					};

				if (activePopup) {
					activePopup.on(PopupEvent.CLOSED, this, () => {
						openPopup();
					});
				} else {
					openPopup();
				}
				transition.finally(() => {
					this.hasConfirmedExit = false;
				});
			}
		}
	}
}
